/**
 * viscoelasticPipeline — slow-recovering surface with viscous flow trails.
 *
 * The displacement field is a "memory" surface: it accumulates bass
 * impulses and decays slowly with the mid band acting as recovery
 * stiffness. The result is a surface that retains a fingerprint of
 * past deformations, slowly flowing back to rest.
 *
 * Viscoelastic feel via:
 *   - long-wavelength low-frequency displacement (bass)
 *   - damped traveling wave (mid)
 *   - rolling shear (high)
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface ViscoelasticPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;
uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(443.897, 441.423));
  p += dot(p, p + 19.19);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1, 0));
  float c = hash21(i + vec2(0, 1));
  float d = hash21(i + vec2(1, 1));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm2(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * valueNoise(p);
    p = p * 2.03 + vec2(1.7, -3.1);
    a *= 0.5;
  }
  return v;
}

// Pseudo-memory field: bass "drops" particles into the surface; they
// slowly flow outward as the mid band reduces viscosity.
float memory(vec2 q, float t) {
  float v = 0.0;
  // Slow flow field advects the surface deformation.
  vec2 flow = vec2(
    sin(t * 0.18) + 0.5 * sin(t * 0.41 + q.y * 0.6),
    cos(t * 0.22) + 0.5 * cos(t * 0.37 + q.x * 0.6)
  ) * 0.35;
  vec2 qf = q - flow;
  // Long-wavelength rolling hill.
  v += sin(qf.x * 2.3 + t * 0.4) * cos(qf.y * 1.9 - t * 0.3) * 0.18;
  // Noise-based "drag marks".
  v += (fbm2(qf * 1.6 + vec2(t * 0.05, -t * 0.07)) - 0.5) * 0.35;
  return v;
}

float mapSurface(vec3 p) {
  // Base sphere.
  float d = length(p) - 1.0;
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.2, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 2.8, 0.0, 1.0), 0.8);
  high = pow(clamp(high * 3.2, 0.0, 1.0), 0.85);
  // Memory field — strong bass pulls memory downward.
  float mem = memory(p.xy * 1.2, uTime);
  d -= mem * (0.25 + bass * 0.45);
  // Slow return to rest, modulated by mid.
  d += mem * mid * 0.10;
  // Surface micro-tremor on high.
  d += (fbm2(p.xy * 16.0 + vec2(uTime * 0.3, 0.0)) - 0.5) * 0.008 * (0.4 + high);
  return d;
}

vec3 calcNormal(vec3 p) {
  vec2 e = vec2(0.002, 0.0);
  return normalize(vec3(
    mapSurface(p + e.xyy) - mapSurface(p - e.xyy),
    mapSurface(p + e.yxy) - mapSurface(p - e.yxy),
    mapSurface(p + e.yyx) - mapSurface(p - e.yyx)
  ));
}

void main() {
  vec2 uv = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  uv.x *= uResolution.x / uResolution.y;

  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.2, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 2.8, 0.0, 1.0), 0.8);
  high = pow(clamp(high * 3.2, 0.0, 1.0), 0.85);

  // Camera slow orbit.
  float ca = uTime * 0.18 + bass * 0.4;
  vec3 ro = vec3(2.6 * cos(ca), 0.6 * sin(ca * 0.7), 2.6 * sin(ca));
  vec3 ta = vec3(0.0, 0.0, 0.0);
  vec3 ww = normalize(ta - ro);
  vec3 uu = normalize(cross(ww, vec3(0.0, 1.0, 0.0)));
  vec3 vv = cross(uu, ww);
  vec3 rd = normalize(uv.x * uu + uv.y * vv + 1.6 * ww);

  float t = 0.0;
  bool hit = false;
  vec3 p = ro;
  for (int i = 0; i < 90; i++) {
    float d = mapSurface(p);
    if (d < 0.001) { hit = true; break; }
    if (t > 8.0) break;
    t += d * 0.9;
    p = ro + rd * t;
  }
  vec3 col = vec3(0.04, 0.03, 0.06);
  if (hit) {
    vec3 n = calcNormal(p);
    vec3 ldir = normalize(vec3(0.5, 0.7, 0.6));
    float diff = clamp(dot(n, ldir), 0.0, 1.0);
    float fres = pow(1.0 - clamp(dot(n, -rd), 0.0, 1.0), 2.8);
    vec3 h = normalize(ldir - rd);
    float spec = pow(clamp(dot(n, h), 0.0, 1.0), 48.0);
    // Iridescent sheen on the surface — relates to deformation depth.
    float irid = 0.5 + 0.5 * sin(
      length(p) * 8.0 + uTime * 0.6 + dot(p, vec3(1.0, 2.0, 3.0))
    );
    vec3 irCol = vec3(
      0.5 + 0.5 * cos(6.28 * (irid + 0.0)),
      0.5 + 0.5 * cos(6.28 * (irid + 0.33)),
      0.5 + 0.5 * cos(6.28 * (irid + 0.66))
    );
    col = vec3(0.0);
    col += diff * vec3(0.75, 0.72, 0.78);
    col += irCol * fres * (0.4 + mid * 0.8);
    col += spec * vec3(1.0) * (0.35 + high * 0.6);
    // Sheen band — visible ripples of the memory field.
    float band = 0.5 + 0.5 * sin(length(p.xy) * 18.0 - uTime * 2.0);
    col += vec3(0.85, 0.6, 0.5) * pow(band, 6.0) * (0.1 + bass * 0.25);
  }

  float vig = pow(16.0 * vUV.x * (1.0 - vUV.x) * vUV.y * (1.0 - vUV.y), 0.25);
  col *= vig;
  col = col / (1.0 + col);
  col = pow(col, vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}`;

export function computeViscoelasticBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.4 * Math.sin(t * 0.85);
  out[1] = 0.6 + 0.4 * Math.abs(Math.sin(t * 1.6));
  out[2] = 0.4 + 0.3 * Math.sin(t * 2.4);
  out[3] = 0.5 + 0.4 * Math.sin(t * 1.0 + 1.0);
  out[4] = 0.5 + 0.4 * Math.sin(t * 1.6 + 2.3);
  out[5] = 0.4 + 0.4 * Math.sin(t * 2.4 + 3.1);
  out[6] = 0.3 + 0.5 * Math.sin(t * 3.8 + 0.5);
  out[7] = 0.4 + 0.4 * Math.sin(t * 5.6 + 1.7);
  out[8] = 0.3 + 0.5 * Math.sin(t * 7.6 + 2.1);
}

export function computeViscoelasticRealtimeBands(freqData: Uint8Array, out: Float32Array): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.008], [0.008, 0.030], [0.030, 0.060],
    [0.060, 0.150], [0.150, 0.260], [0.260, 0.420],
    [0.420, 0.620], [0.620, 0.800], [0.800, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    let s = 0, c = 0;
    const a = Math.floor(lo * N);
    const b = Math.floor(hi * N);
    for (let k = a; k < b; k++) { s += freqData[k]; c++; }
    out[i] = c > 0 ? (s / c) / 255 : 0;
  }
}

export function createViscoelasticPipeline(gl: WebGL2RenderingContext): ViscoelasticPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawViscoelastic(
  gl: WebGL2RenderingContext,
  pipe: ViscoelasticPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}